import { Card } from "@coherence-design-system/controls";
import { Pivot, PivotItem, Stack } from "@fluentui/react";
import { FluentProvider, OptionOnSelectData, webLightTheme } from "@fluentui/react-components";
import { CardContent } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DisplayAPIsException from "../../../BasicComponents/DisplayAPIsExceptionAlert";
import AIInsightsButtonWithPanel from "../../../helper/AIInsightsButtonWithPanel";
import { logEvent, logException } from "../../../helper/AppInsightsHelper";
import { IArrayKey } from "../../../interfaces/CarouselProps.interfaces";
import { IProductFamily, ISentinelDrilldownRawData, ISentinelDrilldownResponse } from "../../../interfaces/customer-product-v2.interfaces";
import { CustomFilterPill } from "../../../SharedComponents/CustomFilterPill";
import { useGetProductKPIsQuery, useGetTenantsMdcProductKPIsQuery, useGetSentinelDrilldownDataQuery } from "../../../slices/ExploreCustomers.slice";
import { useGetUIEnablementsSettingsQuery } from "../../../slices/Home.slice";
import { arrSortDescByKeyLength, buildDisabledFeatureLookup } from "../../../Utils/js-common";
import CustomDropdown from "../../ProductView/Common/CustomDropdown";
import MdcProductTelemetryView from "./MdcProductTelemetry";
import { ProductTelemetryChart } from "./ProductTelemetryChart";
import { ProductTelemetryDailyChart } from "./ProductTelemetryDailyChart";
import ProductTelemetryShimmerLoader from "./ProductTelemetryShimmerLoader";
import SentinelDrilldownCharts from "./SentinelDrilldownCharts";
import { pivotStyles, tokens } from "./ProductTelemetryView.styles";

export default function ProductTelemetryView(props: any) {
  const DAILY_FAMILIES = ["Microsoft Purview Consumption", "Microsoft Sentinel", "Security Copilot"];

  const [searchParams, setSearchParams] = useSearchParams();
  const tenantId = searchParams.get("id") ?? undefined;

  // Initialize selectedFamily from URL parameter or default
  const defaultFamily = "Microsoft Purview";
  const [selectedFamily, setSelectedFamily] = useState(() => {
    const section = searchParams.get("section");
    return section ? section : sessionStorage.getItem("productTelemetry_lastViewedFamily") ?? defaultFamily;
  });

  const [sortedData, setSortedData] = useState<IProductFamily[]>([]);
  const [selectedGranularity, setSelectedGranularity] = useState<string | undefined>("Daily");
  const [selectedApplication, setSelectedApplication] = useState<string | undefined>(undefined);
  const [selectedWorkspace, setSelectedWorkspace] = useState<string[] | undefined>(["AllUp"]);

  const { data, isFetching, error } = useGetProductKPIsQuery(tenantId ?? "") as {
    data?: IProductFamily[];
    isFetching: boolean;
    error: any;
  };
  const {
    data: mdcData,
    isLoading: isMdcFetching,
    error: mdcError
  } = useGetTenantsMdcProductKPIsQuery(
    { tenantIds: [tenantId ?? ""] },
    { skip: selectedFamily !== "Microsoft Defender for Cloud" || !tenantId }
  );

  // Sentinel drilldown is a large payload and is only rendered when the
  // "Microsoft Sentinel" family is selected. Load it lazily on selection so it
  // doesn't compete with the default (Purview) view's calls on initial page load.
  const {
    data: sentinelDrilldownResponse,
    isFetching: isSentinelDrilldownFetching,
    error: sentinelDrilldownError
  } = useGetSentinelDrilldownDataQuery(
    { tenantId: tenantId ?? "" },
    { skip: selectedFamily !== "Microsoft Sentinel" || !tenantId }
  );

  // Extract raw data and Kusto links from the prefetched response
  const sentinelDrilldownRawData = sentinelDrilldownResponse?.data ?? [];
  const sentinelDrilldownKustoLinks = useMemo(() => ({
    tableKustoLinkDaily: sentinelDrilldownResponse?.tableKustoLinkDaily ?? "",
    regionKustoLinkDaily: sentinelDrilldownResponse?.regionKustoLinkDaily ?? "",
    tableKustoLinkMonthly: sentinelDrilldownResponse?.tableKustoLinkMonthly ?? "",
    regionKustoLinkMonthly: sentinelDrilldownResponse?.regionKustoLinkMonthly ?? ""
  }), [sentinelDrilldownResponse]);

  // Log error if Sentinel drilldown fetch fails
  useEffect(() => {
    if (sentinelDrilldownError) {
      logException("Customer Page - Product Telemetry Tab", "ProductTelemetryView", "useGetSentinelDrilldownDataQuery", sentinelDrilldownError);
    }
  }, [sentinelDrilldownError]);

  // Client-side filtering and aggregation of Sentinel drilldown data
  const sentinelDrilldownData: ISentinelDrilldownResponse | null = useMemo(() => {
    if (sentinelDrilldownRawData.length === 0) return null;

    let filteredData = sentinelDrilldownRawData;

    // Filter by aggregationType based on selected granularity
    // Map granularity selection to aggregationType in data
    if (selectedGranularity) {
      filteredData = filteredData.filter((d) =>
        d.aggregationType?.toLowerCase() === selectedGranularity.toLowerCase()
      );
    }

    // Filter by workspaces if specific ones selected
    if (selectedWorkspace && selectedWorkspace.length > 0 && !selectedWorkspace.includes("AllUp")) {
      // Workspace format from API: "WORKSPACENAME : guid"
      // selectedWorkspace contains just the workspace names or full strings
      filteredData = filteredData.filter((d) => {
        const wsName = d.workspace?.split(":")[0]?.trim().toLowerCase() ?? "";
        const fullWs = d.workspace?.trim().toLowerCase() ?? "";
        return selectedWorkspace.some(
          (sw) => sw.toLowerCase() === wsName || sw.toLowerCase() === fullWs
        );
      });
    }
    // If AllUp or no filter, include all workspace data

    // Group by Region and calculate average currentValue
    const byRegionMap = new Map<string, number[]>();
    filteredData.forEach((d) => {
      if (d.region) {
        const values = byRegionMap.get(d.region) || [];
        values.push(d.currentValue);
        byRegionMap.set(d.region, values);
      }
    });

    // Group by Table and calculate average currentValue
    const byTableMap = new Map<string, number[]>();
    filteredData.forEach((d) => {
      if (d.table) {
        const values = byTableMap.get(d.table) || [];
        values.push(d.currentValue);
        byTableMap.set(d.table, values);
      }
    });

    // Calculate averages and sort
    const byRegion = Array.from(byRegionMap.entries())
      .map(([name, values]) => ({
        name,
        value: values.reduce((a, b) => a + b, 0) / values.length
      }))
      .sort((a, b) => b.value - a.value);

    const byTable = Array.from(byTableMap.entries())
      .map(([name, values]) => ({
        name,
        value: values.reduce((a, b) => a + b, 0) / values.length
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10); // Top 10 tables

    return { byRegion, byTable };
  }, [sentinelDrilldownRawData, selectedGranularity, selectedWorkspace]);

  // Enhanced setSelectedFamily that also updates URL
  const handleFamilyChange = (family: string) => {
    setSelectedFamily(family);
    sessionStorage.setItem("productTelemetry_lastViewedFamily", family);

    // Get current URL parameters and update the section parameter
    const currentParams = Object.fromEntries(searchParams.entries());
    setSearchParams({
      ...currentParams,
      section: family
    });
  };
  const uiEnablementData = useGetUIEnablementsSettingsQuery(undefined)?.data || [];
  const disabled = buildDisabledFeatureLookup(uiEnablementData);
  const disabledSections = disabled["section"] || [];

  // Update selectedFamily when URL changes
  useEffect(() => {
    const sectionFromUrl = searchParams.get("section");
    if (sectionFromUrl && sectionFromUrl !== selectedFamily) {
      setSelectedFamily(sectionFromUrl);
    }
  }, [searchParams]);

  // update selectedFamily if default family is not present in data
  useEffect(() => {
    if (sortedData.length > 0) {
      const checkDefaultFamily = sortedData.find((family) => family.name === defaultFamily); // Check if default family exists in data
      if (!checkDefaultFamily) {
        setSelectedFamily(sortedData[0].name); // Set to first family in sorted data
      }
    }
  }, [sortedData]);

  const getDropdownAggregationTypes = (apiResponse: IProductFamily[]) => {
    const types = new Set<string>();
    apiResponse?.forEach((product) => {
      if (DAILY_FAMILIES.includes(product.name)) {
        product.data?.forEach((category) =>
          category.data?.forEach((metric) =>
            metric.data?.forEach((point) => {
              const type = point.aggregationType?.trim();
              if (type && type !== "Weekly") types.add(type);
            })
          )
        );
      }
    });
    return Array.from(types).map((type) => ({ value: type, text: type }));
  };

  const getApplicationsByAggregation = (apiResponse: IProductFamily[], selectedFamily: string) => {
    const apps = new Set<string>();
    const selectedProduct = apiResponse.find((product) => product.name === selectedFamily);

    selectedProduct?.data?.forEach((category) =>
      category.data?.forEach((metric) =>
        metric.data?.forEach((point) => {
          const app = point.application?.trim();
          if (app) apps.add(app);
        })
      )
    );

    return Array.from(apps).map((type) => ({ value: type, text: type }));
  };

  const getWorkspacesBySentinelFamily = (apiResponse: IProductFamily[]) => {
    const workspaces = new Set<string>();
    const sentinelProduct = apiResponse.find((product) => product.name === "Microsoft Sentinel");
    const workspacesWithDailyData = new Set<string>();
    const workspacesWithMonthlyData = new Set<string>();

    sentinelProduct?.data?.forEach((category) =>
      category.data?.forEach((metric) =>
        metric.data?.forEach((point) => {
          const workspace = point.workspace?.trim();
          if (!workspace) return;
          workspaces.add(workspace);
          switch (point.aggregationType?.trim()) {
            case "Daily":
              workspacesWithDailyData.add(workspace);
              break;
            case "Monthly":
              workspacesWithMonthlyData.add(workspace);
              break;
            default:
              break;
          }
        })
      )
    );

    // Filter workspaces based on selected granularity - only show workspaces with data
    const workspacesWithData = Array.from(workspaces).filter((ws) => {
      if (selectedGranularity === "Daily") {
        return workspacesWithDailyData.has(ws);
      } else if (selectedGranularity === "Monthly") {
        return workspacesWithMonthlyData.has(ws);
      }
      return true;
    });

    // Sort workspaces with "AllUp" first, then GUIDs alphabetically
    const sortedWorkspaces = workspacesWithData.sort((a, b) => {
      if (a === "AllUp") return -1;
      if (b === "AllUp") return 1;
      return a.localeCompare(b);
    });

    return sortedWorkspaces.map((ws) => ({
      value: ws,
      text: ws,
      noData: false
    }));
  };

  const aggregationTypeOptions = useMemo(() => (data ? getDropdownAggregationTypes(data) : []), [data]);
  const applicationTypeOptions = useMemo(
    () => (data && selectedFamily ? getApplicationsByAggregation(data, selectedFamily) : []),
    [data, selectedFamily]
  );
  const workspaceOptions = useMemo(() => (data ? getWorkspacesBySentinelFamily(data) : []), [data, selectedGranularity]);

  // Set initial defaults
  useEffect(() => {
    if (aggregationTypeOptions.length > 0 && !aggregationTypeOptions.find((opt) => opt.value === selectedGranularity)) {
      setSelectedGranularity(aggregationTypeOptions[0].value);
    }
  }, [aggregationTypeOptions]);

  // Set initial defaults
  useEffect(() => {
    if (data && selectedFamily) {
      const newOptions = getApplicationsByAggregation(data, selectedFamily);

      if (newOptions.length > 0) {
        const allUpOption = newOptions.find((opt) => opt.value === "AllUp");
        setSelectedApplication(allUpOption ? allUpOption.value : newOptions[0].value);
      } else {
        setSelectedApplication(undefined);
      }
    }
  }, [selectedFamily, data]);

  // Set workspace default when family changes or data loads
  useEffect(() => {
    if (selectedFamily === "Microsoft Sentinel" && workspaceOptions.length > 0) {
      const allUpOption = workspaceOptions.find((opt) => opt.value === "AllUp");
      setSelectedWorkspace([allUpOption ? allUpOption.value : workspaceOptions[0].value]);
    }
  }, [selectedFamily, workspaceOptions]);

  useEffect(() => {
    if (data) {
      const filteredCustomerFamilies = data.filter(
        ({ name }) =>
          !disabledSections
            .filter(({ feature, tab }) => feature === "Explore Customers" && tab === "Product Telemetry")
            .map((m) => m.section)
            .includes(name)
      );

      setSortedData(
        arrSortDescByKeyLength({
          key: "data",
          data: JSON.parse(JSON.stringify(filteredCustomerFamilies))
        } as IArrayKey)
      );
    }

    if (error) {
      logException("Customer Page - Product Telemetry Tab", "ProductTelemetryView", "useGetProductKPIsQuery", error);
    }
  }, [data, isFetching, error]);

  useEffect(() => {
    if (mdcError) {
      logException(
        "Customer Page - Product Telemetry Tab",
        "ProductTelemetryView",
        "useGetTenantsMdcProductKPIsMutation",
        mdcError
      );
    }
  }, [mdcError, isMdcFetching]);

  useEffect(() => {
    const customProps = {
      productFamily: selectedFamily,
      tenantId: tenantId,
      tenantName: props.tenantName
    };
    logEvent("Product Telemetry", "Customer Page", "ProductTelemetryView", customProps);
  }, [selectedFamily]);

  const handleSelectedGranularity = useCallback((selectedItems: string[] | undefined) => {
    setSelectedGranularity(selectedItems?.[0]);
    setSelectedWorkspace(["AllUp"]); // Reset workspace selection when granularity changes
  }, []);

  const handleSelectedApplication = useCallback(({ optionText }: OptionOnSelectData) => {
    setSelectedApplication(optionText);
  }, []);

  const handleSelectedWorkspace = useCallback(
    (selectedItems: string[] | undefined) => {
      if (!selectedItems || selectedItems.length === 0) {
        // If nothing selected, default to AllUp
        setSelectedWorkspace(["AllUp"]);
        return;
      }

      const prevSelection = selectedWorkspace || [];
      const wasAllUpSelected = prevSelection.includes("AllUp");
      const isAllUpNowSelected = selectedItems.includes("AllUp");
      const hasOtherWorkspaces = selectedItems.some((ws) => ws !== "AllUp");

      if (isAllUpNowSelected && hasOtherWorkspaces) {
        if (wasAllUpSelected) {
          // User added a specific workspace while AllUp was selected -> remove AllUp
          setSelectedWorkspace(selectedItems.filter((ws) => ws !== "AllUp"));
        } else {
          // User selected AllUp while other workspaces were selected -> keep only AllUp
          setSelectedWorkspace(["AllUp"]);
        }
      } else {
        setSelectedWorkspace(selectedItems);
      }
    },
    [selectedWorkspace]
  );

  const toSelectedKeysMap = (values?: (string | undefined)[]): { [key: string]: boolean } => {
    const result: { [key: string]: boolean } = {};

    if (!values) return result;

    for (const v of values) {
      if (typeof v === "string" && v.trim() !== "") {
        result[v] = true;
      }
    }
    return result;
  };

  // Custom rendering of options to show tooltip and disable options with no data
  const renderWorkspaceMenuList = useCallback(
    (menuListProps?: any, defaultRender?: any) => {
      if (menuListProps?.items?.length) {
        menuListProps.items = menuListProps.items.map((item: any) => {
          const itemKey = (item?.key ?? "").toString();
          const itemText = (item?.text ?? "").toString();
          const matchingOption = workspaceOptions.find((option) => {
            const optionKey = (option.value ?? option.text ?? "").toString();
            const optionText = (option.text ?? "").toString();
            return optionKey === itemKey || optionText === itemText;
          });

          if (!matchingOption?.noData) return item;

          return {
            ...item,
            title: "No data available for this workspace",
            iconProps: { iconName: "Info", style: { color: "#999" } },
            style: { color: "#999", fontWeight: "lighter" }
          };
        });
      }

      return typeof defaultRender === "function" ? defaultRender(menuListProps) : null;
    },
    [workspaceOptions]
  );

  const displayFilters = DAILY_FAMILIES.includes(selectedFamily) ? (
    <FluentProvider theme={webLightTheme}>
      <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "8px", marginLeft: 12, padding: "0 10px" }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          {selectedFamily === "Microsoft Sentinel" ? (
            <CustomFilterPill
              name="Data Granularity"
              defaultValue="--Select--"
              singleSelect
              menuProps={{
                items: aggregationTypeOptions.map((option) => ({
                  key: option.value ?? option.text ?? "",
                  text: option.text ?? ""
                }))
              }}
              selectedKeys={toSelectedKeysMap([selectedGranularity])}
              onApply={handleSelectedGranularity}
              onResetFilters={() => setSelectedGranularity(undefined)}
            />)
            : (
              <CustomDropdown
                defaultOption={{
                  optionText: selectedGranularity,
                  optionValue: selectedGranularity,
                  selectedOptions: [selectedGranularity || ""]
                }}
                onSelectedOption={({ optionText }) => handleSelectedGranularity(optionText ? [optionText] : undefined)}
                data={aggregationTypeOptions}
                styles={{ root: { minWidth: 100 } }}
                label="Data Granularity"
              />
            )}
        </div>
        {selectedFamily === "Microsoft Sentinel" && workspaceOptions.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", marginLeft: "16px" }}>
            <CustomFilterPill
              name="Workspace"
              defaultValue="--Select--"
              menuProps={{
                items: workspaceOptions.map((option) => ({
                  key: option.value ?? option.text ?? "",
                  text: option.text ?? ""
                })),
                onRenderMenuList: renderWorkspaceMenuList
              }}
              styles={{
                subComponentStyles: {
                  contextualMenu: {
                    container: {
                      overflow: "hidden"
                    },
                    list: {
                      maxHeight: "280px",
                      overflowY: "auto",
                      overflowX: "hidden"
                    }
                  }
                }
              }}
              selectedKeys={toSelectedKeysMap(selectedWorkspace)}
              onApply={handleSelectedWorkspace}
              onResetFilters={() => setSelectedWorkspace(["AllUp"])}
              selectAllButton={{
                show: workspaceOptions.filter((ws) => ws.value !== "AllUp").length > 0,
                label: "Select All Workspaces",
                selectValues: workspaceOptions.filter((ws) => ws.value !== "AllUp").map((ws) => ws.value),
                onClick: () => setSelectedWorkspace(workspaceOptions.filter((ws) => ws.value !== "AllUp").map((ws) => ws.value))
              }}
              clearAllButton={{
                show: selectedWorkspace ? !selectedWorkspace.includes("AllUp") : false,
                label: "Clear Selection (Show AllUp)",
                clearToValue: ["AllUp"],
                onClick: () => setSelectedWorkspace(["AllUp"])
              }}
            />
          </div>
        )}
        {["Microsoft Purview Consumption"].includes(selectedFamily) && (
          <div style={{ display: "flex", alignItems: "center" }}>
            {(() => {
              return (
                <CustomDropdown
                  defaultOption={{
                    optionText: selectedApplication,
                    optionValue: selectedApplication,
                    selectedOptions: [selectedApplication ?? ""]
                  }}
                  onSelectedOption={handleSelectedApplication}
                  data={applicationTypeOptions}
                  styles={{ root: { minWidth: 100 } }}
                  label="Application"
                />
              );
            })()}
          </div>
        )}
      </div>
    </FluentProvider>
  ) : null;

  return (
    <>
      {(isMdcFetching || isFetching) && <ProductTelemetryShimmerLoader />}

      {!isMdcFetching && !isFetching && !error && !mdcError && sortedData.length === 0 && (
        <span style={{ marginLeft: "45%", marginTop: 50, background: "#FFF", lineHeight: 8 }}>
          No Product Data found for the given Tenant Id
        </span>
      )}

      {!isMdcFetching && !isFetching && !error && sortedData.length > 0 && (
        <div className="p-4">
          <Stack
            horizontal
            horizontalAlign="space-between"
            verticalAlign="start"
            tokens={tokens.sectionStack}
            styles={{
              root: {
                flexWrap: "nowrap",
                gap: "10px"
              }
            }}
          >
            <div style={{ flex: "1 1 auto", minWidth: "0", maxWidth: "calc(100% - 85px)", overflowX: "auto" }}>
              <Pivot
                selectedKey={selectedFamily}
                onLinkClick={(item) => item && handleFamilyChange(item.props.itemKey!)}
                className="mb-4"
                styles={pivotStyles}
              >
                {sortedData
                  .filter((family) => family.name !== "Microsoft Defender for Cloud")
                  .map((family) => (
                    <PivotItem key={family.name} itemKey={family.name} headerText={family.name} />
                  ))}
                {data && data.length > 0 && data[0].hasMdcData && (
                  <PivotItem
                    key="Microsoft Defender for Cloud"
                    itemKey="Microsoft Defender for Cloud"
                    headerText="Microsoft Defender for Cloud"
                  />
                )}
              </Pivot>
            </div>

            <div style={{ flexShrink: 0, marginTop: "20px", position: "sticky", top: "20px" }}>
              <AIInsightsButtonWithPanel
                buttonStyle={{ left: "0px", marginRight: 25 }}
                metricId="2"
                panelTitle="Product Insights"
                tenantId={tenantId}
                tenantName={props.tenantName}
              />
            </div>
          </Stack>

          {displayFilters}

          {(selectedFamily === "Microsoft Defender for Cloud" && (
            <>
              <MdcProductTelemetryView
                data={mdcData?.filter(({ tenantId: id }) => id === tenantId).flatMap((m) => m.productFamilies) ?? []}
              />
              {mdcError && <DisplayAPIsException error={mdcError} height={250} />}
            </>
          )) ||
            (sortedData.length > 0 && (
              <Card>
                <CardContent>
                  {sortedData
                    .filter((x) => x.name === selectedFamily)
                    .map(({ name: pName, data: pData }) => {
                      const chartProps = {
                        granularity: selectedGranularity,
                        application: selectedApplication,
                        workspace: selectedWorkspace,
                        selectedFamily,
                        // M365 Copilot has no Features Telemetry backend mapping
                        // (getFeatureData 500s for it), so hide the Explore
                        // Features button for that family only.
                        disableExploreFeaturesButton: selectedFamily === "M365 Copilot",
                        carouseldata: {
                          name: "",
                          items: pData.map(
                            ({
                              name: kpiName,
                              data: kpiData,
                              kustoLink: kpiKustoLink,
                              isCAT,
                              isTCT,
                              hasEngagedAdmin
                            }) => {
                              const normalizedSelectedApplication = selectedApplication?.trim().toLowerCase();
                              const normalizedSelectedWorkspace = selectedWorkspace?.map((ws) => ws.trim().toLowerCase()) || [];

                              // Filter by application for Purview Consumption
                              let filteredKpiData = ["Microsoft Purview Consumption"].includes(selectedFamily)
                                ? kpiData
                                  .map((kpi) => ({
                                    ...kpi,
                                    data: kpi.data.filter(
                                      (point) =>
                                        point.application?.trim().toLowerCase() === normalizedSelectedApplication
                                    )
                                  }))
                                  .filter((kpi) => kpi.data.length > 0)
                                : kpiData;

                              // Filter and aggregate by workspace for Microsoft Sentinel
                              if (selectedFamily === "Microsoft Sentinel" && selectedWorkspace) {
                                const isMultipleWorkspacesSelected = selectedWorkspace.length > 1 && !selectedWorkspace.includes("AllUp");

                                // First, filter data points by selected workspaces
                                filteredKpiData = filteredKpiData
                                  .map((kpi) => ({
                                    ...kpi,
                                    data: kpi.data.filter(
                                      (point) =>
                                        normalizedSelectedWorkspace.includes(point.workspace?.trim().toLowerCase() || "")
                                    )
                                  }))
                                  .filter((kpi) => kpi.data.length > 0);

                                // If multiple workspaces selected, merge metrics with same name and aggregate values
                                if (isMultipleWorkspacesSelected && filteredKpiData.length > 0) {
                                  const metricsByName = new Map<string, typeof filteredKpiData[0]>();

                                  filteredKpiData.forEach((kpi) => {
                                    const metricName = kpi.name.trim();

                                    if (metricsByName.has(metricName)) {
                                      // Merge data points into existing metric
                                      const existing = metricsByName.get(metricName)!;
                                      existing.data = [...existing.data, ...kpi.data];
                                    } else {
                                      // Create new entry with a copy of kpi
                                      metricsByName.set(metricName, { ...kpi, data: [...kpi.data] });
                                    }
                                  });

                                  // Aggregate data points by date for each metric
                                  filteredKpiData = Array.from(metricsByName.values()).map((kpi) => {
                                    const aggregatedMap = new Map<string, { x: Date | number; y: number; aggregationType?: string }>();

                                    kpi.data.forEach((point) => {
                                      const dateKey = point.x instanceof Date ? point.x.toISOString() : String(point.x);
                                      const aggType = point.aggregationType?.trim() || "";
                                      const key = `${dateKey}_${aggType}`;

                                      if (aggregatedMap.has(key)) {
                                        const existing = aggregatedMap.get(key)!;
                                        existing.y += Number(point.y) || 0;
                                      } else {
                                        aggregatedMap.set(key, {
                                          x: point.x,
                                          y: Number(point.y) || 0,
                                          aggregationType: point.aggregationType
                                        });
                                      }
                                    });

                                    return {
                                      ...kpi,
                                      data: Array.from(aggregatedMap.values())
                                    };
                                  });
                                }
                              }

                              return {
                                name: kpiName,
                                data: filteredKpiData,
                                kustoLink: kpiKustoLink || "",
                                isTCT: isTCT,
                                isCAT: isCAT,
                                hasEngagedAdmin: hasEngagedAdmin
                              };
                            }
                          )
                        },
                        tenantId: tenantId
                      };
                      return selectedFamily === "Microsoft Sentinel" ? (
                        // Sentinel: For Monthly, render all 3 charts in one row; For Daily, stacked vertically
                        selectedGranularity === "Daily" ? (
                          <React.Fragment key={`sentinel-daily-${pName}-${selectedApplication}-${selectedGranularity}-${selectedWorkspace}`}>
                            <ProductTelemetryDailyChart {...chartProps} />
                            <SentinelDrilldownCharts
                              data={sentinelDrilldownData}
                              isLoading={isSentinelDrilldownFetching}
                              granularity={selectedGranularity || "Daily"}
                              kustoLink={chartProps.carouseldata.items[0]?.kustoLink}
                              tableKustoLink={sentinelDrilldownKustoLinks.tableKustoLinkDaily}
                              regionKustoLink={sentinelDrilldownKustoLinks.regionKustoLinkDaily}
                            />
                          </React.Fragment>
                        ) : (
                          // Monthly: Build mainChartData from carouseldata for inline rendering
                          (() => {
                            const item = chartProps.carouseldata.items[0];
                            if (!item) return null;

                            // Build line chart data (same logic as ProductTelemetryChart)
                            const lineChartData = item.data?.map((metric, i) => {
                              const filteredPoints = metric.data?.filter(point =>
                                point.aggregationType === "Monthly"
                              ) || [];
                              return {
                                legend: metric.name,
                                data: filteredPoints.map(point => ({
                                  x: new Date(point.x),
                                  y: Number(point.y)
                                })),
                                color: (window as any).fluentCharting?.getNextColor?.(i) || ["#0078d4", "#107c10", "#d13438"][i % 3],
                                lineOptions: { lineBorderWidth: "4" }
                              };
                            }) ?? [];

                            // Build variances
                            const variances = item.data?.filter(metric =>
                              metric.data?.some(point => point.aggregationType === "Monthly")
                            ).map(metric => ({
                              name: metric.name,
                              description: metric.description,
                              variance: metric.variance
                            })) ?? [];

                            return (
                              <SentinelDrilldownCharts
                                key={`sentinel-monthly-${pName}-${selectedApplication}-${selectedGranularity}-${selectedWorkspace}`}
                                data={sentinelDrilldownData}
                                isLoading={isSentinelDrilldownFetching}
                                granularity={selectedGranularity || "Monthly"}
                                kustoLink={item.kustoLink}
                                tableKustoLink={sentinelDrilldownKustoLinks.tableKustoLinkMonthly}
                                regionKustoLink={sentinelDrilldownKustoLinks.regionKustoLinkMonthly}
                                mainChartData={{
                                  title: item.name,
                                  lineChartData: lineChartData,
                                  variances: variances,
                                  isTCT: item.isTCT,
                                  isCAT: item.isCAT,
                                  hasEngagedAdmin: item.hasEngagedAdmin,
                                  kustoLink: item.kustoLink
                                }}
                              />
                            );
                          })()
                        )
                      ) : selectedGranularity === "Daily" && DAILY_FAMILIES.includes(selectedFamily) ? (
                        <ProductTelemetryDailyChart
                          key={`daily-${pName}-${selectedApplication}-${selectedGranularity}-${selectedWorkspace}`}
                          {...chartProps}
                        />
                      ) : (
                        <ProductTelemetryChart
                          key={`monthly-${pName}-${selectedApplication}-${selectedGranularity}-${selectedWorkspace}`}
                          {...chartProps}
                        />
                      );
                    })}
                </CardContent>
              </Card>
            ))}
        </div>
      )}

      {error && <DisplayAPIsException error={error} height={250} />}
    </>
  );
}
